import { getInitials } from "@/utils/generic";

export type ColourTokenId =
  | "slate"
  | "amber"
  | "rose"
  | "pink"
  | "violet"
  | "blue"
  | "teal"
  | "green";

export type ColourSurface = "workspace" | "role" | "tag";

export interface ColourToken {
  id: ColourTokenId;
  name: string;
  /** Lightest fill — chip backgrounds (roles, tags). */
  soft: string;
  /** Muted fill — avatars and picker swatches. */
  tint: string;
  /** Foreground colour that sits on `soft`/`tint` at >=7:1 contrast. */
  text: string;
}

// The pickable palette — the swatches the three colour pickers offer.
// `slate` is deliberately absent here (see NEUTRAL_COLOUR_TOKEN below).
export const COLOUR_TOKENS: readonly ColourToken[] = [
  {
    id: "amber",
    name: "Amber",
    soft: "#FFFBEB",
    tint: "#FEF3C7",
    text: "#92400E",
  },
  {
    id: "rose",
    name: "Rose",
    soft: "#FFF1F2",
    tint: "#FFE4E6",
    text: "#9F1239",
  },
  {
    id: "pink",
    name: "Pink",
    soft: "#FDF2F8",
    tint: "#FCE7F3",
    text: "#9D174D",
  },
  {
    id: "violet",
    name: "Violet",
    soft: "#F5F3FF",
    tint: "#EDE9FE",
    text: "#5B21B6",
  },
  {
    id: "blue",
    name: "Blue",
    soft: "#EFF6FF",
    tint: "#DBEAFE",
    text: "#1E40AF",
  },
  {
    id: "teal",
    name: "Teal",
    soft: "#F0FDFA",
    tint: "#CCFBF1",
    text: "#115E59",
  },
  {
    id: "green",
    name: "Green",
    soft: "#F0FDF4",
    tint: "#DCFCE7",
    text: "#166534",
  },
] as const;

// Neutral grey. Not offered in the picker, but it is the render-time fallback
// for anything we can't map (unknown token id, arbitrary API hex, the legacy
// "no colour" fill). Rendering an unrecognised value grey is honest — falling
// back to a real palette colour would confidently claim a colour the user
// never chose.
export const NEUTRAL_COLOUR_TOKEN: ColourToken = {
  id: "slate",
  name: "Slate",
  soft: "#F8FAFC",
  tint: "#F1F5F9",
  text: "#334155",
};

// The colour a fresh entity's picker starts on (the picker can't select the
// neutral token). Distinct from the render-time fallback above.
export const DEFAULT_COLOUR_TOKEN = COLOUR_TOKENS[0];

// Maps every hex the three legacy palettes ever stored (old workspace
// GROUP_COLORS, role ROLE_COLORS, tag TAG_COLORS) onto a new token, so
// existing rows keep rendering without a data migration.
const LEGACY_HEX_TO_ID: Record<string, ColourTokenId> = {
  // GROUP_COLORS (saturated workspace fills)
  "#fec84b": "amber",
  "#84caff": "blue",
  "#faa7e0": "pink",
  "#fda29b": "rose",
  "#6ce9a6": "green",
  "#4687b9": "blue",
  // ROLE_COLORS (tinted role fills)
  "#f0f9ff": "blue",
  "#f0fdfa": "teal",
  "#fff7ed": "amber",
  "#fefce8": "amber",
  "#faf5ff": "violet",
  "#f0fdf4": "green",
  "#fdf2f8": "pink",
  "#ecfeff": "blue",
  // TAG_COLORS
  "#fedf89": "amber",
  "#b2ddff": "blue",
  "#fcceee": "pink",
  "#fecdca": "rose",
  "#d9d6fe": "violet",
  // Legacy "no colour" tag fill
  "#f5f5f5": "slate",
};

const ALL_TOKENS: readonly ColourToken[] = [
  ...COLOUR_TOKENS,
  NEUTRAL_COLOUR_TOKEN,
];

const BY_ID = new Map(ALL_TOKENS.map((t) => [t.id, t]));
const BY_HEX = new Map<string, ColourToken>();
for (const token of ALL_TOKENS) {
  BY_HEX.set(token.soft.toLowerCase(), token);
  BY_HEX.set(token.tint.toLowerCase(), token);
}

/**
 * Resolve a stored colour to a token, or `undefined` when the value maps to no
 * known token id, current token hex, or legacy hex. Callers that render a
 * swatch should prefer {@link resolveColourToken}; callers that hold their own
 * stored colours (e.g. role meta) use this to fall through to those on a miss.
 */
export function tryResolveColourToken(
  value?: string | null
): ColourToken | undefined {
  if (!value) return undefined;
  const key = value.trim().toLowerCase();
  const legacy = LEGACY_HEX_TO_ID[key];
  return (
    BY_ID.get(key as ColourTokenId) ??
    BY_HEX.get(key) ??
    (legacy ? BY_ID.get(legacy) : undefined)
  );
}

/**
 * Resolve a stored colour value to a token. Accepts a token id (the new
 * format), any legacy hex from the three old palettes, or a current token
 * hex. Unknown values fall back to the neutral token so callers never render
 * an empty swatch — and never mislabel an unpicked value as a real colour.
 */
export function resolveColourToken(value?: string | null): ColourToken {
  return tryResolveColourToken(value) ?? NEUTRAL_COLOUR_TOKEN;
}

/**
 * The token id a picker should start on for a stored value: the value's own
 * colour when it maps to a pickable swatch, otherwise the default. The neutral
 * token isn't pickable, so it also falls through to the default.
 */
export function defaultPickerColourId(value?: string | null): ColourTokenId {
  const token = tryResolveColourToken(value);
  if (!token || token.id === NEUTRAL_COLOUR_TOKEN.id) {
    return DEFAULT_COLOUR_TOKEN.id;
  }
  return token.id;
}

export const PLACEHOLDER_INITIAL = "A";

/**
 * Live initials for a swatch/avatar: two letters for workspaces (matches the
 * table avatar), one for roles and tags (matches the chip). Placeholder while
 * the name field is empty.
 */
export function colourInitials(name: string, surface: ColourSurface): string {
  const letters = surface === "workspace" ? 2 : 1;
  return getInitials(name ?? "", letters) || PLACEHOLDER_INITIAL;
}
